import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Zap, PenTool, Play, Edit2, Loader2, AlertCircle, CheckCircle2,
  FileText, ListTodo, Users,
} from 'lucide-react';
import { getTokenUsage } from '../../lib/notion-api';
import { AGENTS, SOURCE_AGENTS, CATEGORY_AGENT, CHATROOM_AGENT } from '../../data/agents';

// ── 에이전트 현황 보드용 데이터 (공유 모듈 재사용) ──
const AGENT_MINI = AGENTS;

// ── 오피스 탭과 상태 공유 (탭 마운트 시점 무관) ──
function _syncAgentWork(agent, working) {
  if (!window.__caioAgentState) window.__caioAgentState = { agents: {}, meeting: null };
  window.__caioAgentState.agents[agent] = working;
  window.dispatchEvent(new CustomEvent('caio-agent-work', { detail: { agent, working } }));
}
function _syncMeeting(detail) {
  if (!window.__caioAgentState) window.__caioAgentState = { agents: {}, meeting: null };
  window.__caioAgentState.meeting = detail.active ? detail : null;
  window.dispatchEvent(new CustomEvent('caio-meeting', { detail }));
}

// ═══ 건강블로그 글 작성 대기 배너 ═══
// 탭 전환해도 작업 유지 — module-level 전역 상태
const _hw = { items: null, open: false, writing: {}, results: {} };
const _hwListeners = new Set();
function _hwNotify() { _hwListeners.forEach(cb => cb()); }

function HealthWriteBanner({ data, onRefresh }) {
  const stuckCount = data?.health?.byStatus?.['크롤링 완료'] || 0;
  const [, tick] = React.useReducer(x => x + 1, 0);
  const [listLoading, setListLoading] = React.useState(false);

  // 마운트 시 구독 — 탭 복귀 후에도 최신 상태 즉시 반영
  React.useEffect(() => {
    _hwListeners.add(tick);
    return () => _hwListeners.delete(tick);
  }, [tick]);

  const { items, open, writing, results } = _hw;

  if (stuckCount === 0 && Object.keys(results).length === 0) return null;

  const loadItems = async () => {
    if (items) { _hw.open = !_hw.open; _hwNotify(); return; }
    setListLoading(true);
    try {
      const res = await fetch('/api/health-write');
      const d = await res.json();
      _hw.items = d.items || [];
      _hw.open = true;
      _hwNotify();
    } finally { setListLoading(false); }
  };

  const writeOne = async (pageId) => {
    _hw.writing = { ..._hw.writing, [pageId]: 'loading' };
    _hwNotify();
    _syncAgentWork('healthy', true);
    _syncMeeting({ active: true, mission: 'health_write', agents: ['healthy'], label: '건강블로그 글 작성' });
    try {
      const res = await fetch('/api/health-write', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageId }),
      });
      const d = await res.json();
      if (d.success) {
        _hw.writing = { ..._hw.writing, [pageId]: 'done' };
        _hw.results = { ..._hw.results, [pageId]: { 블로그제목: d.블로그제목 } };
        _hw.items = _hw.items?.filter(i => i.id !== pageId);
        _hwNotify();
        onRefresh?.();
      } else throw new Error(d.error);
    } catch (e) {
      _hw.writing = { ..._hw.writing, [pageId]: 'error' };
      _hw.results = { ..._hw.results, [pageId]: { error: e.message } };
      _hwNotify();
    } finally {
      const stillWorking = Object.values(_hw.writing).some(s => s === 'loading');
      if (!stillWorking) {
        _syncAgentWork('healthy', false);
        _syncMeeting({ active: false });
      }
    }
  };

  const writeAll = async () => {
    if (!items?.length) return;
    for (const item of items) {
      if (writing[item.id] === 'loading' || writing[item.id] === 'done') continue;
      await writeOne(item.id);
    }
  };

  const pendingItems = items?.filter(i => writing[i.id] !== 'done') || [];
  const anyLoading = Object.values(writing).some(s => s === 'loading');

  return (
    <div className="n-card n-health-write-banner" style={{ marginBottom: 12 }}>
      {/* 배너 헤더 */}
      <div className="n-health-write-header" onClick={loadItems} style={{ cursor: 'pointer' }}
        role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && loadItems()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PenTool size={14} style={{ color: 'var(--status-crawl)' }} />
          <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--txt)' }}>
            건강블로그 글 작성 대기
          </span>
          <span className="n-hw-badge">{stuckCount}건</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {pendingItems.length > 0 && open && (
            <button className="n-btn-primary" style={{ fontSize: 11, padding: '4px 12px' }}
              onClick={e => { e.stopPropagation(); writeAll(); }}
              disabled={anyLoading}>
              {anyLoading ? <><Loader2 size={11} className="n-spin" /> 작성 중…</> : <><Play size={11} /> 전체 글 작성</>}
            </button>
          )}
          <span style={{ fontSize: 10, color: 'var(--txt3)' }}>
            {listLoading ? <Loader2 size={11} className="n-spin" /> : open ? '▲' : '▼ 목록 보기'}
          </span>
        </div>
      </div>

      {/* 항목 목록 */}
      {open && items && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--brd)', paddingTop: 10 }}>
          {pendingItems.length === 0 && Object.keys(results).length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--txt3)', textAlign: 'center', padding: '8px 0' }}>항목 없음</div>
          )}
          {pendingItems.map(item => {
            const st = writing[item.id];
            return (
              <div key={item.id} className="n-health-resume-item" style={{ marginBottom: 6 }}>
                <div className="n-health-resume-info">
                  <div className="n-health-resume-title">{item.제목 || '제목 없음'}</div>
                  {item.날짜 && <div className="n-health-resume-date">{item.날짜}</div>}
                </div>
                <button className={`n-health-resume-btn ${st || 'idle'}`}
                  onClick={() => writeOne(item.id)}
                  disabled={st === 'loading' || st === 'done'}>
                  {st === 'loading' && <><Loader2 size={11} className="n-spin" /> 작성 중</>}
                  {st === 'done' && <><CheckCircle2 size={11} /> 완료</>}
                  {st === 'error' && <><AlertCircle size={11} /> 재시도</>}
                  {!st && <><PenTool size={11} /> 글 작성</>}
                </button>
              </div>
            );
          })}
          {Object.entries(results).map(([pageId, r]) => (
            <div key={pageId} className={`n-health-resume-result ${r.error ? 'error' : 'success'}`}>
              {r.error ? <><AlertCircle size={11} /> 오류: {r.error}</> : <><CheckCircle2 size={11} /> 완료: {r.블로그제목}</>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TokenUsage() {
  const [usage, setUsage] = React.useState(() => getTokenUsage());
  const [flash, setFlash] = React.useState(false);
  const [confirmReset, setConfirmReset] = React.useState(false);

  React.useEffect(() => {
    const refresh = () => {
      setUsage(getTokenUsage());
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 700);
      return () => clearTimeout(t);
    };
    window.addEventListener('caio-token-update', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('caio-token-update', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const handleReset = () => {
    if (!confirmReset) { setConfirmReset(true); setTimeout(() => setConfirmReset(false), 3000); return; }
    localStorage.removeItem('caio_token_usage');
    setUsage(getTokenUsage());
    setConfirmReset(false);
    window.dispatchEvent(new CustomEvent('caio-token-update'));
  };

  const [editingBudget, setEditingBudget] = React.useState(false);
  const [budgetInput, setBudgetInput] = React.useState('');
  const budgetRef = useRef(null);

  const { totals, history } = usage;
  const budget = parseFloat(localStorage.getItem('caio_budget') || '5.00');
  const spent = totals.cost;
  const remaining = Math.max(0, budget - spent);
  const usedPct = Math.min(100, (spent / budget) * 100);
  const today = new Date().toISOString().split('T')[0];
  const todayCalls = history.filter(h => h.time?.startsWith(today));
  const todayCost = todayCalls.reduce((sum, h) => sum + (h.cost || 0), 0);
  const recentCalls = history.slice(-5).reverse();
  const barColor = usedPct > 80 ? 'var(--red)' : usedPct > 50 ? 'var(--orange)' : 'var(--green)';

  const startEditBudget = () => {
    setBudgetInput(budget.toFixed(2));
    setEditingBudget(true);
    setTimeout(() => budgetRef.current?.select(), 50);
  };
  const saveBudget = () => {
    const val = parseFloat(budgetInput);
    if (!isNaN(val) && val > 0) {
      localStorage.setItem('caio_budget', val.toFixed(2));
      window.dispatchEvent(new CustomEvent('caio-token-update'));
    }
    setEditingBudget(false);
  };

  return (
    <div className={`n-card n-token-card${flash ? ' n-token-flash' : ''}`} style={{ marginBottom: 16 }}>
      <div className="n-token-header">
        <div className="n-section-title" style={{ marginBottom: 0 }}><Zap size={14} /><span>API 토큰 사용량</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className={`n-token-reset-btn ${confirmReset ? 'confirm' : ''}`} onClick={handleReset}>
            {confirmReset ? '정말 리셋?' : '리셋'}
          </button>
          <div className="n-token-live">LIVE</div>
        </div>
      </div>
      <div className="n-token-grid">
        <div className="n-token-item">
          <div className="n-token-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            충전 잔액
            <button onClick={startEditBudget} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--txt3)', padding: 0, display: 'flex' }} title="충전금액 수정">
              <Edit2 size={10} />
            </button>
          </div>
          {editingBudget ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
              <span style={{ fontSize: 13, color: 'var(--txt2)' }}>$</span>
              <input
                ref={budgetRef}
                value={budgetInput}
                onChange={e => setBudgetInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveBudget(); if (e.key === 'Escape') setEditingBudget(false); }}
                onBlur={saveBudget}
                style={{
                  width: 70, background: 'var(--bg3)', border: '1px solid var(--gold)',
                  borderRadius: 5, color: 'var(--txt)', fontSize: 14, padding: '2px 6px',
                  fontFamily: 'Outfit, sans-serif', fontWeight: 600,
                }}
              />
            </div>
          ) : (
            <div className="n-token-value"
              onClick={startEditBudget} title="클릭해서 수정"
              role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && startEditBudget()}
              style={{ cursor: 'pointer', color: remaining > 1 ? 'var(--green)' : 'var(--red)' }}>
              ${remaining.toFixed(2)}
            </div>
          )}
        </div>
        <div className="n-token-item">
          <div className="n-token-label">이번달 사용</div>
          <div className="n-token-value" style={{ color: 'var(--orange)' }}>${spent.toFixed(4)}</div>
        </div>
        <div className="n-token-item">
          <div className="n-token-label">오늘 비용</div>
          <div className="n-token-value">${todayCost.toFixed(4)}</div>
        </div>
        <div className="n-token-item">
          <div className="n-token-label">총 충전</div>
          <div className="n-token-value" style={{ fontSize: 13, cursor: 'pointer', color: 'var(--txt2)' }} onClick={startEditBudget}
            role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && startEditBudget()}>
            ${budget.toFixed(2)} <Edit2 size={9} style={{ opacity: 0.5 }} />
          </div>
        </div>
      </div>
      <div className="n-token-progress">
        <div className="n-token-bar-track"
          role="progressbar"
          aria-valuenow={Math.round(usedPct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`AI 토큰 사용량 ${usedPct.toFixed(1)}%`}
        >
          <div className="n-token-bar-fill" style={{ width: `${usedPct}%`, background: barColor }} />
        </div>
        <div className="n-token-bar-labels">
          <span>{usedPct.toFixed(1)}% 사용 · ${budget.toFixed(2)} 충전</span>
          <span>{totals.input.toLocaleString()} in / {totals.output.toLocaleString()} out</span>
        </div>
      </div>
      {recentCalls.length > 0 && (
        <div className="n-token-history">
          <div className="n-token-history-label">최근 호출</div>
          {recentCalls.map((h, i) => (
            <div key={i} className={`n-token-call${i === 0 && flash ? ' n-token-call-new' : ''}`}>
              <span className="n-token-call-source">{h.source}</span>
              <span className="n-token-call-tokens">{h.input?.toLocaleString()}+{h.output?.toLocaleString()} tok</span>
              <span className="n-token-call-cost">${h.cost?.toFixed(5)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ 에이전트 현황 보드 ═══
function AgentStatusBoard({ data }) {
  const today = new Date().toISOString().split('T')[0];
  const todos = data?.todos?.recent || [];

  // 에이전트별 오늘 완료 / 대기 집계
  const agentStats = {};
  AGENT_MINI.forEach(a => { agentStats[a.id] = { done: 0, pending: 0 }; });
  todos.forEach(t => {
    const aid = CATEGORY_AGENT[t.카테고리] || CHATROOM_AGENT[t.담당채팅방] || null;
    if (!aid || !agentStats[aid]) return;
    if (t.상태 === '완료') agentStats[aid].done++;
    else agentStats[aid].pending++;
  });

  // 에이전트별 토큰 합산 (오늘, 소스 기준 근사값)
  const tokenUsage = getTokenUsage();
  const agentTokens = {};
  AGENT_MINI.forEach(a => { agentTokens[a.id] = 0; });
  tokenUsage.history.forEach(h => {
    if (!h.time?.startsWith(today)) return;
    const agents = h.agentIds || SOURCE_AGENTS[h.source];
    if (!agents || !agents.length) return;
    const share = ((h.input || 0) + (h.output || 0)) / agents.length;
    agents.forEach(aid => { if (agentTokens[aid] !== undefined) agentTokens[aid] += share; });
  });
  const maxTokens = Math.max(...Object.values(agentTokens), 1);

  const totalDone = todos.filter(t => t.상태 === '완료').length;
  const totalPending = todos.filter(t => t.상태 !== '완료').length;

  return (
    <div className="n-card" style={{ marginBottom: 16 }}>
      <div className="n-section-title" style={{ marginBottom: 12 }}>
        <Users size={14} /><span>에이전트 현황 보드</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--txt3)', fontFamily: 'Outfit', fontVariantNumeric: 'tabular-nums' }}>
          오늘 완료 <b style={{ color: 'var(--green)' }}>{totalDone}</b> · 대기 <b style={{ color: 'var(--orange)' }}>{totalPending}</b>
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
        {AGENT_MINI.map(agent => {
          const stats = agentStats[agent.id];
          const tokens = agentTokens[agent.id];
          const tokenPct = Math.min(100, (tokens / maxTokens) * 100);
          const status = tokens > 0 ? (stats.pending > 0 ? 'active' : 'done') : 'idle';
          const dotColor = status === 'active' ? 'var(--green)' : status === 'done' ? 'var(--accent)' : 'var(--txt3)';

          return (
            <div key={agent.id} style={{
              background: 'var(--bg)', border: '1px solid var(--brd)',
              borderRadius: 'var(--radius-sm)', padding: '9px 9px 7px',
              display: 'flex', flexDirection: 'column', gap: 4,
            }}>
              {/* 이름 + 상태 dot */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--txt)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.name}</span>
              </div>
              {/* 역할 */}
              <div style={{ fontSize: 10, color: 'var(--txt3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{agent.role}</div>
              {/* 완료 / 대기 카운트 */}
              <div style={{ display: 'flex', gap: 5, fontSize: 10, fontFamily: 'Outfit', fontVariantNumeric: 'tabular-nums' }}>
                {stats.done > 0 && (
                  <span style={{ color: 'var(--green)', fontWeight: 600 }}>✓{stats.done}</span>
                )}
                {stats.pending > 0 && (
                  <span style={{ color: 'var(--orange)', fontWeight: 600 }}>{stats.pending}대기</span>
                )}
                {stats.done === 0 && stats.pending === 0 && (
                  <span style={{ color: 'var(--txt3)' }}>—</span>
                )}
              </div>
              {/* 토큰 미니 바 */}
              {tokens > 0 && (
                <div>
                  <div style={{ height: 3, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${tokenPct}%`, background: agent.color, borderRadius: 2, opacity: 0.75 }} />
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--txt3)', fontFamily: 'Outfit', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                    {Math.round(tokens).toLocaleString()} tok
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══ 에이전트별 AI 비용 차트 (오늘) ═══
const SOURCE_LABEL = {
  'agent-process':    '할일 처리',
  'agent-chain':      '매출 분석',
  'agent-brainstorm': '아이디어 회의',
  'agent-plan':       '목표 분해',
  'health-write':     '건강블로그 작성',
  'keyword-analyze':  '키워드 분석',
  'keyword-monitor':  '키워드 모니터',
};
const SOURCE_BAR_COLOR = {
  'agent-process':    'var(--agent-dash)',
  'agent-chain':      'var(--gold)',
  'agent-brainstorm': 'var(--agent-thread)',
  'agent-plan':       'var(--green)',
  'health-write':     'var(--agent-healthy)',
  'keyword-analyze':  'var(--agent-booker)',
  'keyword-monitor':  'var(--agent-booker)',
};

function AgentCostChart() {
  const [usage, setUsage] = React.useState(() => getTokenUsage());
  const [budget, setBudget] = React.useState(() => parseFloat(localStorage.getItem('caio_budget') || '5.0'));
  const [editBudget, setEditBudget] = React.useState(false);
  const [budgetInput, setBudgetInput] = React.useState('');

  React.useEffect(() => {
    const refresh = () => setUsage(getTokenUsage());
    window.addEventListener('caio-token-update', refresh);
    return () => window.removeEventListener('caio-token-update', refresh);
  }, []);

  const saveBudget = () => {
    const v = parseFloat(budgetInput.replace(/[^0-9.]/g, ''));
    if (!isNaN(v) && v > 0) {
      localStorage.setItem('caio_budget', v);
      setBudget(v);
    }
    setEditBudget(false);
  };

  const today = new Date().toISOString().split('T')[0];
  const thisMonth = new Date().toISOString().slice(0, 7);
  const todayHistory = usage.history.filter(h => h.time?.startsWith(today));
  const monthHistory = usage.history.filter(h => (h.time || '').startsWith(thisMonth));
  const monthCost = monthHistory.reduce((s, h) => s + (h.cost || 0), 0);
  const budgetPct = Math.min(100, (monthCost / budget) * 100);
  const budgetColor = budgetPct >= 90 ? 'var(--red)' : budgetPct >= 70 ? 'var(--orange)' : 'var(--green)';

  if (todayHistory.length === 0 && monthCost === 0) return null;

  // 소스별 비용 집계
  const sourceCosts = {};
  todayHistory.forEach(h => {
    const src = h.source || 'unknown';
    sourceCosts[src] = (sourceCosts[src] || 0) + (h.cost || 0);
  });

  const entries = Object.entries(sourceCosts).sort(([, a], [, b]) => b - a).slice(0, 7);
  const maxCost = Math.max(...entries.map(([, c]) => c), 0.00001);
  const totalToday = todayHistory.reduce((s, h) => s + (h.cost || 0), 0);

  return (
    <div className="n-card" style={{ marginBottom: 16 }}>
      <div className="n-section-title" style={{ marginBottom: 10 }}>
        <Zap size={14} /><span>AI 비용</span>
        <span style={{ marginLeft: 'auto', fontFamily: 'Outfit', fontSize: 13, fontWeight: 700, color: 'var(--orange)', fontVariantNumeric: 'tabular-nums' }}>
          ${totalToday.toFixed(4)} 오늘
        </span>
      </div>

      {/* 이번달 예산 */}
      <div style={{ marginBottom: 12, padding: '8px 10px', background: 'var(--bg3)', borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: 'var(--txt3)' }}>이번달 누적</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'Outfit', fontSize: 13, fontWeight: 700, color: budgetColor, fontVariantNumeric: 'tabular-nums' }}>
              ${monthCost.toFixed(3)}
            </span>
            <span style={{ fontSize: 10, color: 'var(--txt3)' }}>/</span>
            {editBudget ? (
              <input
                autoFocus
                type="text"
                value={budgetInput}
                onChange={e => setBudgetInput(e.target.value)}
                onBlur={saveBudget}
                onKeyDown={e => { if (e.key === 'Enter') saveBudget(); if (e.key === 'Escape') setEditBudget(false); }}
                style={{ width: 52, background: 'var(--bg2)', border: '1px solid var(--gold)', borderRadius: 4, color: 'var(--gold)', fontSize: 12, padding: '1px 5px', fontFamily: 'Outfit', textAlign: 'right', outline: 'none' }}
              />
            ) : (
              <button
                onClick={() => { setBudgetInput(budget.toString()); setEditBudget(true); }}
                style={{ background: 'none', border: 'none', fontFamily: 'Outfit', fontSize: 11, color: 'var(--txt3)', cursor: 'pointer', padding: 0 }}
                title="예산 임계값 설정">
                ${budget.toFixed(0)} 예산
              </button>
            )}
          </div>
        </div>
        <div style={{ height: 4, background: 'var(--bg)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${budgetPct}%`, background: budgetColor, borderRadius: 2, transition: 'width 0.4s ease' }} />
        </div>
        <div style={{ fontSize: 10, color: 'var(--txt3)', marginTop: 2, fontFamily: 'Outfit', fontVariantNumeric: 'tabular-nums' }}>
          {budgetPct.toFixed(0)}% 사용
        </div>
      </div>

      {todayHistory.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {entries.map(([source, cost]) => {
            const pct = (cost / maxCost) * 100;
            const label = SOURCE_LABEL[source] || source;
            const barColor = SOURCE_BAR_COLOR[source] || 'var(--accent)';
            const tokenCount = todayHistory.filter(h => h.source === source).reduce((s, h) => s + (h.input || 0) + (h.output || 0), 0);
            return (
              <div key={source}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                  <span style={{ fontSize: 11, color: 'var(--txt2)' }}>{label}</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontFamily: 'Outfit', fontSize: 10, color: 'var(--txt3)', fontVariantNumeric: 'tabular-nums' }}>{tokenCount.toLocaleString()} tok</span>
                    <span style={{ fontFamily: 'Outfit', fontSize: 11, fontWeight: 600, color: 'var(--txt)', fontVariantNumeric: 'tabular-nums' }}>${cost.toFixed(5)}</span>
                  </div>
                </div>
                <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 2 }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ marginTop: 8, fontSize: 10, color: 'var(--txt3)' }}>
        * 에이전트 매핑은 API 출처 기준 근사값
      </div>
    </div>
  );
}

function StatusBanner({ data }) {
  if (!data) return null;
  const { summary, todos } = data;
  const tokenUsage = getTokenUsage();
  const budget = parseFloat(localStorage.getItem('caio_budget') || '5.00');
  const usedPct = Math.min(100, (tokenUsage.totals.cost / budget) * 100);
  const items = [
    { icon: FileText, label: `오늘 발행 ${summary.todayPosts}건`, state: summary.todayPosts > 0 ? 'good' : 'dim' },
    { icon: ListTodo, label: `미완료 ${todos.pending}건`, state: todos.pending > 5 ? 'warn' : todos.pending > 0 ? 'dim' : 'good' },
    { icon: Zap, label: `토큰 ${usedPct.toFixed(0)}%`, state: usedPct > 80 ? 'danger' : usedPct > 50 ? 'warn' : 'good' },
  ];
  return (
    <div className="n-status-banner">
      {items.map((it, i) => (
        <span key={i} className={`n-banner-item ${it.state}`}><it.icon size={11} />{it.label}</span>
      ))}
    </div>
  );
}

export { HealthWriteBanner, TokenUsage, AgentStatusBoard, AgentCostChart, StatusBanner };
